// Site-header scroll behaviour — shared across every interactive surface
// (in-app site viewer, standalone runtime) plus the static SSR site.
//
// Three behaviours, chosen per-site via the website spec's `header_scroll`:
//   • 'static'    — the header flows with the page and scrolls out of view.
//   • 'sticky'    — the header is pinned to the top and always visible.
//   • 'auto-hide' — pinned, but slides out of view (translateY(-100%)) once the
//                   visitor scrolls DOWN past the header height and slides back
//                   in as soon as they start scrolling UP. A small delta
//                   threshold avoids jitter.
//
// React surfaces use `useHeaderScroll(behavior)` and attach the returned
// callback ref to their header element. Returning a *callback* ref (rather than
// an object ref + effect) is deliberate: the header element frequently mounts
// AFTER the host component first renders (while the manifest/page is still
// loading), and a plain effect keyed on the behaviour would run once against a
// null element and never re-attach. The callback ref fires the moment React
// attaches the node — and again on unmount / behaviour change — so the scroll
// wiring is always bound to the live element.
//
// The static SSR site (no React/hydration) inlines `AUTO_HIDE_HEADER_CSS` +
// `autoHideHeaderScript()` and marks the header wrapper with the
// `pg-sticky-header` class (sticky) plus `data-pg-auto-hide` (auto-hide).

import { useCallback, useRef, useState } from 'react'
import type { UIEvent } from 'react'

// Scroll-delta (px) past which a direction change toggles the header. Keeps a
// tiny scroll wobble from flickering the bar.
const DELTA = 6

export type HeaderScrollBehavior = 'static' | 'sticky' | 'auto-hide'

/**
 * The same scroll-direction reveal, for a toolbar above a scroll container of
 * its OWN rather than the page (a list pane, a repository's browse area).
 * `useHeaderScroll` above listens on `window` and pins with `position: sticky`,
 * neither of which applies inside a flex column that scrolls internally — the
 * direction rule is what the two share, so it lives here beside it.
 *
 * Returns the state and the handler; the caller decides how "hidden" looks
 * (collapse, slide) and owns the markup. Also returns `reveal` — call it when
 * something inside the bar takes focus (the repository's `/` shortcut jumps to
 * a search box that may be collapsed) and whenever the list is replaced, so a
 * result set too short to scroll can never strand the bar out of view.
 */
export function useScrollDirectionHide(options?: {
  revealAbove?: number
  /**
   * Least overflow (px) the scroller must have before the bar is allowed to
   * hide — pass the bar's own measured height.
   *
   * Without it the two chase each other whenever the list is only slightly
   * taller than the pane: hiding hands the bar's height BACK to the list, the
   * list then fits, the browser snaps `scrollTop` to 0, "near the top" reveals
   * the bar, and the list overflows again. Hiding only when there is more to
   * scroll than hiding reclaims breaks that loop at its source, rather than
   * damping it with a timer.
   */
  minOverflow?: number
}) {
  const { revealAbove = 24, minOverflow = 0 } = options || {}
  const [hidden, setHidden] = useState(false)
  const lastY = useRef(0)

  const reveal = useCallback(() => setHidden(false), [])

  const onScroll = useCallback((event: UIEvent<HTMLElement>) => {
    const el = event.currentTarget
    const y = el.scrollTop
    const dy = y - lastY.current
    lastY.current = y
    // Measured while the bar is still expanded (the gate below only applies to
    // the transition INTO hidden), so this is the real room to give back.
    const overflow = el.scrollHeight - el.clientHeight
    setHidden((was) => {
      // Near the top the bar is always present — reading the first rows should
      // not depend on which direction you arrived from.
      if (y <= revealAbove) return false
      if (dy > DELTA) return overflow >= minOverflow ? true : was
      if (dy < -DELTA) return false
      return was
    })
  }, [revealAbove, minOverflow])

  return { hidden, onScroll, reveal }
}


/**
 * Resolve the effective header scroll behaviour from the site manifest/spec.
 * Honours an explicit `header_scroll` when present; otherwise falls back to the
 * legacy `header_auto_hide` boolean (true / unset ⇒ auto-hide, false ⇒ a plain
 * always-visible sticky header — its historical meaning). Shared by every
 * surface so the in-app viewer, runtime client and SSR agree.
 */
export function resolveHeaderScroll(
  headerScroll?: string | null,
  headerAutoHide?: boolean | null,
): HeaderScrollBehavior {
  if (headerScroll === 'static' || headerScroll === 'sticky' || headerScroll === 'auto-hide') {
    return headerScroll
  }
  return headerAutoHide === false ? 'sticky' : 'auto-hide'
}

/**
 * Drive header scroll behaviour on a header element. Returns a callback ref to
 * attach to the header (or its sticky wrapper) — `ref={useHeaderScroll(...)}`.
 *
 *  • 'static'    — clears any sticky positioning; the header scrolls away.
 *  • 'sticky'    — pins the header to the top, always visible.
 *  • 'auto-hide' — pins + hides on scroll-down / reveals on scroll-up.
 */
export function useHeaderScroll<T extends HTMLElement = HTMLElement>(behavior: HeaderScrollBehavior) {
  // Holds the teardown for the currently-bound element (scroll listener +
  // inline-style reset). Cleared on unmount or behaviour change.
  const cleanupRef = useRef<(() => void) | null>(null)

  return useCallback((el: T | null) => {
    // Tear down any previous wiring before (re)binding — the callback ref fires
    // with `null` on unmount and on every behaviour change.
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null }
    if (!el || typeof window === 'undefined') return

    if (behavior === 'static') {
      // Header flows with the page — clear anything a prior behaviour set.
      el.style.position = ''
      el.style.top = ''
      el.style.transform = ''
      el.style.transition = ''
      return
    }

    // 'sticky' and 'auto-hide' are both pinned to the top.
    el.style.position = 'sticky'
    el.style.top = '0'
    if (!el.style.zIndex) el.style.zIndex = '50'
    const unpin = pinFromStickySection(el)

    if (behavior === 'sticky') {
      el.style.transform = ''
      el.style.transition = ''
      cleanupRef.current = unpin
      return
    }

    // 'auto-hide' — slide out on scroll-down, slide back in on scroll-up.
    el.style.transition = 'transform 0.3s ease'
    el.style.willChange = 'transform'

    let lastY = window.scrollY
    let hidden = false
    const onScroll = () => {
      const y = window.scrollY
      const dy = y - lastY
      const h = el.offsetHeight || 0
      if (y <= h) {
        // At / near the top — always reveal.
        if (hidden) { el.style.transform = 'translateY(0)'; hidden = false }
      } else if (dy > DELTA && !hidden) {
        el.style.transform = 'translateY(-100%)'; hidden = true
      } else if (dy < -DELTA && hidden) {
        el.style.transform = 'translateY(0)'; hidden = false
      }
      lastY = y
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    cleanupRef.current = () => {
      window.removeEventListener('scroll', onScroll)
      el.style.transform = ''
      unpin()
    }
  }, [behavior])
}

/**
 * The header chrome is ONE pinned wrapper, so every section in it used to
 * stick — an announcement bar the author left un-sticky stayed glued to the
 * top with the header under it. When the chrome marks a section sticky
 * (`.pg-sec-sticky`), pin from THAT section: the wrapper's `top` becomes
 * minus everything above it, which scrolls away like the page. No sticky
 * section = the whole chrome is pinned, as before. Re-measured on resize,
 * because the bands above wrap to different heights on a phone. The
 * section's own sticky offset (`stickyOffset` → its computed `top`) is kept:
 * the wrapper pins at `offset - lead`, so the section lands at its offset.
 */
function pinFromStickySection(el: HTMLElement): () => void {
  const measure = () => {
    const first = el.querySelector('.pg-sec-sticky') as HTMLElement | null
    let top = 0
    if (first) {
      const lead = Math.max(0, Math.round(first.getBoundingClientRect().top - el.getBoundingClientRect().top))
      const offset = Math.round(parseFloat(window.getComputedStyle(first).top) || 0)
      top = offset - lead
    }
    el.style.top = top ? `${top}px` : '0'
  }
  measure()
  const RO = (window as unknown as { ResizeObserver?: typeof ResizeObserver }).ResizeObserver
  const ro = RO ? new RO(measure) : null
  if (ro) ro.observe(el)
  else window.addEventListener('resize', measure)
  return () => {
    if (ro) ro.disconnect()
    else window.removeEventListener('resize', measure)
    el.style.top = ''
  }
}

/** The same, for the static site: pins `.pg-sticky-header` from its first
 *  sticky section. Inline `<script>` body (no tags). */
export function headerPinScript(): string {
  return `(function(){
    var el=document.querySelector('.pg-sticky-header');
    if(!el)return;
    function m(){
      var f=el.querySelector('.pg-sec-sticky'),t=0;
      if(f){
        var lead=Math.max(0,Math.round(f.getBoundingClientRect().top-el.getBoundingClientRect().top));
        t=Math.round(parseFloat(window.getComputedStyle(f).top)||0)-lead;
      }
      el.style.top=t?(t+'px'):'0';
    }
    m();
    if(window.ResizeObserver)new ResizeObserver(m).observe(el);
    else window.addEventListener('resize',m);
  })();`
}

// ── Static SSR helpers ────────────────────────────────────────────────────
// The deployed static site has no React; these inline a tiny vanilla
// implementation. The header wrapper carries the `pg-sticky-header` class
// (sticky + auto-hide modes) and `data-pg-auto-hide` (auto-hide only). For the
// 'static' mode the wrapper carries neither, so the header scrolls with the page.

export const AUTO_HIDE_HEADER_CSS = `
  .pg-sticky-header{position:sticky;top:0;z-index:50;}
  .pg-sticky-header[data-pg-auto-hide]{transition:transform .3s ease;will-change:transform;}
`

// ── Header scroll STATE (transparent-over-hero, shrink-on-scroll) ─────────
//
// Distinct from auto-hide, which is about the bar's POSITION. These two are
// about its APPEARANCE as the visitor moves down the page:
//
//   • `data-pg-header-transparent` — the header starts with no background so
//     a full-bleed hero shows through it, then solidifies once the visitor
//     scrolls past the fold. The single most recognisable "modern site"
//     header treatment, and impossible before because the bar always painted
//     its own background.
//   • `data-pg-header-shrink` — the bar reduces its padding (and scales its
//     brand) once scrolled, reclaiming vertical space on long pages.
//
// Both are driven by ONE attribute (`data-pg-scrolled`) that the script
// toggles, so all the styling stays declarative and a failed script leaves
// the header in its solid, readable state.
export const HEADER_STATE_CSS = `
  .pg-sticky-header[data-pg-header-transparent]{
    transition:background-color .28s ease, box-shadow .28s ease, backdrop-filter .28s ease;
  }
  /* Transparent ONLY while at the top. The !important is load-bearing: the
     chrome shell paints its background INLINE, and an inline style outranks
     any stylesheet rule without it. */
  .pg-sticky-header[data-pg-header-transparent]:not([data-pg-scrolled]),
  .pg-sticky-header[data-pg-header-transparent]:not([data-pg-scrolled]) > *{
    background:transparent!important;box-shadow:none!important;border-bottom-color:transparent!important;
  }
  .pg-sticky-header[data-pg-header-transparent][data-pg-scrolled]{
    backdrop-filter:saturate(180%) blur(10px);
    -webkit-backdrop-filter:saturate(180%) blur(10px);
    box-shadow:0 1px 0 var(--pg-border), 0 8px 24px rgba(0,0,0,.06);
  }
  .pg-sticky-header[data-pg-header-shrink]{transition:padding .25s ease}
  .pg-sticky-header[data-pg-header-shrink][data-pg-scrolled]{padding-top:0;padding-bottom:0}
  .pg-sticky-header[data-pg-header-shrink][data-pg-scrolled] .pg-site-brand{font-size:1rem}
  @media (prefers-reduced-motion: reduce){
    .pg-sticky-header[data-pg-header-transparent],
    .pg-sticky-header[data-pg-header-shrink]{transition:none}
  }
`

/** How far down the page the header switches to its "scrolled" appearance. */
const SCROLLED_AT = 24

/**
 * Toggle `data-pg-scrolled` on the sticky header. Emitted for the static
 * site; React surfaces call `useHeaderScrollState` below, which shares the
 * same threshold so the two can't disagree.
 */
export function headerScrollStateScript(): string {
  return `(function(){
    var el=document.querySelector('.pg-sticky-header[data-pg-header-transparent],.pg-sticky-header[data-pg-header-shrink]');
    if(!el)return;
    function on(){
      var y=window.scrollY||document.documentElement.scrollTop||0;
      if(y>${SCROLLED_AT})el.setAttribute('data-pg-scrolled','');
      else el.removeAttribute('data-pg-scrolled');
    }
    window.addEventListener('scroll',on,{passive:true});
    on();
  })();`
}

/**
 * React equivalent of the above — a callback ref, for the same reason
 * `useHeaderScroll` is one: the header element frequently mounts after the
 * host's first render, so an effect keyed on a plain ref would bind to null
 * and never re-attach.
 */
export function useHeaderScrollState<T extends HTMLElement = HTMLElement>(enabled: boolean) {
  const cleanupRef = useRef<(() => void) | null>(null)
  return useCallback((el: T | null) => {
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null }
    if (!el || !enabled || typeof window === 'undefined') return
    const onScroll = () => {
      const y = window.scrollY || 0
      if (y > SCROLLED_AT) el.setAttribute('data-pg-scrolled', '')
      else el.removeAttribute('data-pg-scrolled')
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    cleanupRef.current = () => {
      window.removeEventListener('scroll', onScroll)
      el.removeAttribute('data-pg-scrolled')
    }
  }, [enabled])
}

/** Inline `<script>` body (no tags) that wires scroll-direction hide/reveal
 *  for any `.pg-sticky-header[data-pg-auto-hide]` element on a static page. */
export function autoHideHeaderScript(): string {
  return `(function(){
    var el=document.querySelector('.pg-sticky-header[data-pg-auto-hide]');
    if(!el)return;
    var lastY=window.scrollY||0,hidden=false,D=${DELTA};
    window.addEventListener('scroll',function(){
      var y=window.scrollY||0,dy=y-lastY,h=el.offsetHeight||0;
      if(y<=h){if(hidden){el.style.transform='translateY(0)';hidden=false;}}
      else if(dy>D&&!hidden){el.style.transform='translateY(-100%)';hidden=true;}
      else if(dy<-D&&hidden){el.style.transform='translateY(0)';hidden=false;}
      lastY=y;
    },{passive:true});
  })();`
}
